import { useEffect, useState } from 'react';
import { Loader2, UserPlus, Shield, Trash2, AlertTriangle, Crown, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

/**
 * AdminsTab — list / promote / demote admins. Founder-only for write actions.
 * The list itself is visible to every admin (transparency, E9).
 *
 * Backend endpoints:
 *   GET    /api/bloom-gestion-secure/admins
 *   POST   /api/bloom-gestion-secure/admins              {email}
 *   DELETE /api/bloom-gestion-secure/admins/{email}
 *
 * Auth: cookie-based (session_token), no localStorage tokens.
 */
export default function AdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [callerIsFounder, setCallerIsFounder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/admins`, { credentials: 'include' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Impossible de charger la liste des administrateurs.');
        setLoading(false);
        return;
      }
      const data = await res.json();
      setAdmins(data.admins || []);
      setCallerIsFounder(!!data.caller_is_founder);
      setError('');
    } catch (e) {
      setError('Erreur de connexion au serveur.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const promote = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${email} ajouté. Le TOTP sera configuré à sa première connexion Google.`);
        setNewEmail('');
        refresh();
      } else {
        toast.error(data.detail || 'Erreur lors de la promotion');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const demote = async (email) => {
    if (!window.confirm(`Retirer ${email} des administrateurs ?\nToutes ses sessions actives seront immédiatement déconnectées et ses codes de récupération supprimés.`)) {
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/admins/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${email} retiré`);
        refresh();
      } else {
        toast.error(data.detail || 'Erreur lors du retrait');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} /></div>;
  }

  return (
    <div data-testid="admins-tab" className="space-y-6">
      <div>
        <h2 className="text-xl font-serif font-bold mb-1">Administrateurs</h2>
        <p className="text-sm text-[#6B6A68]">{admins.length} administrateur{admins.length > 1 ? 's' : ''} actif{admins.length > 1 ? 's' : ''}</p>
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="admins-error">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /><span>{error}</span>
        </div>
      )}

      {!callerIsFounder && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3" data-testid="non-founder-note">
          Seul le fondateur peut ajouter ou retirer des administrateurs.
        </div>
      )}

      {callerIsFounder && (
        <div className="bg-[#F9F8F6] border border-[#E0DFDD] p-4" data-testid="add-admin-form">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><UserPlus size={16} /> Ajouter un administrateur</h3>
          <p className="text-xs text-[#6B6A68] mb-3">
            La personne devra ensuite se connecter via Google. Elle sera automatiquement guidée pour configurer son TOTP à sa première connexion.
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              disabled={actionLoading}
              placeholder="email@exemple.com"
              className="flex-1 border border-[#E0DFDD] px-3 py-2 text-sm focus:outline-none focus:border-[#1A1514] disabled:opacity-60"
              data-testid="add-admin-email"
            />
            <button
              type="button"
              onClick={promote}
              disabled={actionLoading || !newEmail.trim()}
              className="bg-[#1A1514] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              data-testid="add-admin-submit"
            >
              {actionLoading ? <Loader2 size={14} className="animate-spin" /> : 'Ajouter'}
            </button>
          </div>
        </div>
      )}

      <div className="border border-[#E0DFDD]" data-testid="admins-list">
        <div className="grid grid-cols-12 gap-3 bg-[#F9F8F6] px-4 py-2 text-xs font-medium text-[#6B6A68] uppercase tracking-wide">
          <div className="col-span-5">Email</div>
          <div className="col-span-3">Statut TOTP</div>
          <div className="col-span-2">Codes restants</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {admins.map((a) => (
          <div key={a.email} className="grid grid-cols-12 gap-3 px-4 py-3 border-t border-[#E0DFDD] items-center text-sm" data-testid={`admin-row-${a.email}`}>
            <div className="col-span-5 flex items-center gap-2">
              {a.is_founder ? <Crown size={14} className="text-[#D4AF37] flex-shrink-0" /> : <Mail size={14} className="text-[#6B6A68] flex-shrink-0" />}
              <span className="break-all">{a.email}</span>
              {a.is_self && <span className="text-xs text-[#6B6A68]">(toi)</span>}
            </div>
            <div className="col-span-3">
              {a.tfa_setup_required ? (
                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5">
                  <Shield size={12} /> Setup requis
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-0.5">
                  <Shield size={12} /> Configuré
                </span>
              )}
            </div>
            <div className="col-span-2 text-sm">{a.recovery_codes_remaining ?? 0} / 8</div>
            <div className="col-span-2 text-right">
              {callerIsFounder && !a.is_founder && !a.is_self && (
                <button
                  type="button"
                  onClick={() => demote(a.email)}
                  disabled={actionLoading}
                  className="text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid={`demote-${a.email}`}
                >
                  <Trash2 size={12} /> Retirer
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
